import { createFileRoute, useRouter, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Suspense, useState } from "react";
import { CheckCircle2, XCircle, RefreshCw, FlaskConical, ShieldAlert } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getBokunTestIntegrationStatus,
  getBokunTestBookingOps,
  setBokunTestNewBookings,
  listCustomToursForExport,
  dryRunExportCustomTour,
  runBokunTestReads,
  type DryRunResult,
} from "@/lib/bokun-test-admin.functions";

const statusQuery = queryOptions({
  queryKey: ["admin", "bokun-test", "status"],
  queryFn: () => getBokunTestIntegrationStatus(),
  staleTime: 0,
});

const toursQuery = queryOptions({
  queryKey: ["admin", "bokun-test", "tours"],
  queryFn: () => listCustomToursForExport(),
  staleTime: 60_000,
});

export const Route = createFileRoute("/_authenticated/admin/bokun-test")({
  head: () => ({
    meta: [
      { title: "Bokun · Integración de prueba" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(statusQuery),
  component: BokunTestPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-destructive">{error instanceof Error ? error.message : String(error)}</div>
  ),
});

function BokunTestPage() {
  return (
    <div className="admin-scope min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-semibold sm:text-4xl">
          Bokun · Integración de prueba
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Entorno de pruebas aislado: REST v2 de experiencias, Booking API, App Webhooks con
          HMAC-SHA256 y SellerExternalID. Las escrituras están cerradas por bandera y nunca se
          ejecutan contra producción.
        </p>
        <div className="mt-6">
          <AdminNav />
        </div>
        <div className="mt-6">
          <Button asChild>
            <Link to="/reservar-prueba/$activityId" params={{ activityId: "28141" }}>
              Abrir reserva de prueba
            </Link>
          </Button>
        </div>
        <Suspense fallback={<Skeleton className="mt-8 h-96 w-full rounded-2xl" />}>
          <Body />
        </Suspense>
      </div>
    </div>
  );
}

function Yes({ ok }: { ok: boolean }) {
  return ok ? (
    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
      Sí
    </Badge>
  ) : (
    <Badge variant="secondary">
      <XCircle className="mr-1 h-3.5 w-3.5" />
      No
    </Badge>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

function ReadChecksCard() {
  const run = useServerFn(runBokunTestReads);
  const mutation = useMutation({ mutationFn: () => run() });
  const report = mutation.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <CardTitle className="text-base">
          Fase 1 · autenticación, catálogo y disponibilidad
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${mutation.isPending ? "animate-spin" : ""}`} />
          {mutation.isPending ? "Verificando…" : "Verificar lecturas"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {!report && !mutation.error && (
          <p className="text-xs text-muted-foreground">
            Sólo lecturas contra el servidor de prueba: firma HMAC-SHA1, rechazo de firma inválida,
            búsqueda paginada, detalle de producto y disponibilidad en MXN. No ejecuta escrituras.
          </p>
        )}
        {mutation.error && (
          <p className="text-xs text-destructive">{(mutation.error as Error).message}</p>
        )}
        {report?.checks.map((c) => (
          <div key={c.id} className="flex items-start justify-between gap-4 text-sm">
            <span className="min-w-0">
              <span className="block">{c.label}</span>
              <span className="block break-words text-xs text-muted-foreground">{c.detail}</span>
            </span>
            <Badge
              variant={
                c.status === "ok" ? "default" : c.status === "error" ? "destructive" : "secondary"
              }
            >
              {c.status === "ok" ? "correcto" : c.status === "error" ? "error" : "omitido"}
            </Badge>
          </div>
        ))}
        {report && (
          <p className="pt-2 font-mono text-[11px] text-muted-foreground">
            {report.host || "—"} · {report.correlationId} ·{" "}
            {new Date(report.ranAt).toLocaleString("es-MX")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function BookingOpsCard() {
  const load = useServerFn(getBokunTestBookingOps);
  const toggle = useServerFn(setBokunTestNewBookings);
  const ops = useQuery({
    queryKey: ["admin", "bokun-test", "booking-ops"],
    queryFn: () => load(),
    staleTime: 0,
  });
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => toggle({ data: { enabled } }),
    onSuccess: () => void ops.refetch(),
  });
  const data = ops.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <CardTitle className="text-base">Reservas públicas de prueba</CardTitle>
        {data && (
          <Button
            variant={data.newBookingsEnabled ? "outline" : "default"}
            size="sm"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(!data.newBookingsEnabled)}
          >
            {data.newBookingsEnabled ? "Pausar nuevas reservas" : "Reactivar nuevas reservas"}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <Row label="Nuevas reservas">
          <Yes ok={!!data?.newBookingsEnabled} />
        </Row>
        <p className="text-xs text-muted-foreground">
          Pausar sólo impide reservas nuevas: la confirmación, el registro del pago y la pantalla
          pública de las reservas ya creadas siguen funcionando.
        </p>

        {data && data.alerts.length > 0 && (
          <div className="space-y-2">
            <p className="flex items-center gap-1.5 text-sm font-medium text-destructive">
              <ShieldAlert className="h-4 w-4" /> Reservas en revisión
            </p>
            {data.alerts.map((a) => (
              <div key={a.id} className="rounded-lg border p-2 text-xs">
                <span className="font-medium">{a.code}</span> · {a.reason}
                {a.detail && (
                  <span className="block break-words text-muted-foreground">{a.detail}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {data && data.reservations.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Últimas reservas</p>
            {data.reservations.map((r) => (
              <div key={r.code} className="flex flex-wrap justify-between gap-2 text-xs">
                <span className="font-medium">{r.code}</span>
                <span className="text-muted-foreground">
                  {r.travelDate ?? "—"} · {r.status} · {r.reconciliation}
                  {r.amount != null ? ` · ${r.currency} ${r.amount}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Body() {
  const router = useRouter();
  const { data } = useSuspenseQuery(statusQuery);
  const tours = useSuspenseQuery(toursQuery);
  const [tourId, setTourId] = useState<string>("");
  const [result, setResult] = useState<DryRunResult | null>(null);
  const runDry = useServerFn(dryRunExportCustomTour);

  const dryMutation = useMutation({
    mutationFn: (id: string) => runDry({ data: { tourId: id } }),
    onSuccess: (r) => setResult(r),
  });

  const writesBlocked = !data.writeGuard.allowed;
  const bookingBlocked = !data.bookingGuard.allowed;
  const v2Blocked = !data.v2WriteGuard.allowed;

  return (
    <div className="mt-8 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
          <CardTitle className="text-base">Entorno y banderas</CardTitle>
          <Button variant="outline" size="sm" onClick={() => router.invalidate()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-verificar
          </Button>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="space-y-1">
            <Row label="Entorno">
              <Badge variant={data.environment === "test" ? "secondary" : "destructive"}>
                {data.environment}
              </Badge>
            </Row>
            <Row label="Host">
              <span className="font-mono text-xs">{data.host || "—"}</span>
              {data.hostIsLive && (
                <Badge variant="destructive" className="ml-2">
                  LIVE_HOST_BLOCKED
                </Badge>
              )}
            </Row>
            <Row label="Host de prueba requerido">
              <span className="font-mono text-xs">{data.requiredTestHost}</span>
            </Row>
            <Row label="Hosts permitidos">
              <span className="font-mono text-xs">{data.allowedHosts.join(", ")}</span>
            </Row>
            {data.liveHostsRejected.length > 0 && (
              <Row label="Hosts live descartados">
                <span className="font-mono text-xs">{data.liveHostsRejected.join(", ")}</span>
              </Row>
            )}
            <Row label="Endpoint de webhook">
              <span className="font-mono text-xs">{data.webhookUrl}</span>
            </Row>
            <Row label="Escrituras habilitadas">
              <Yes ok={data.flags.writesEnabled} />
            </Row>
            <Row label="Flujo de reserva de prueba">
              <Yes ok={data.flags.bookingFlowEnabled} />
            </Row>
            <Row label="Esquema REST v2 confirmado">
              <Yes ok={data.flags.v2SchemaConfirmed} />
            </Row>
            <Row label="Esquema Booking API confirmado">
              <Yes ok={data.flags.bookingSchemaConfirmed} />
            </Row>
            <Row label="Token legacy de webhook">
              <Yes ok={data.flags.legacyWebhookTokenEnabled} />
            </Row>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Credenciales de prueba (solo presencia)</p>
            <Row label="Access key">
              <Yes ok={data.credentials.accessKey} />
            </Row>
            <Row label="Secret key">
              <Yes ok={data.credentials.secretKey} />
            </Row>
            <Row label="App API key">
              <Yes ok={data.credentials.appApiKey} />
            </Row>
            <Row label="App secret">
              <Yes ok={data.credentials.appSecret} />
            </Row>
            <Row label="Booking channel">
              <Yes ok={data.credentials.channelUuid} />
            </Row>
            <Row label="Vendor ID">
              <Yes ok={data.credentials.vendorId} />
            </Row>
          </div>
        </CardContent>
      </Card>

      <ReadChecksCard />

      <BookingOpsCard />

      <div className="grid gap-4 md:grid-cols-2">
        {data.capabilities.map((c) => (
          <Card key={c.id}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between gap-3 text-sm">
                <span>{c.label}</span>
                <Yes ok={c.ready} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{c.detail}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {(writesBlocked || bookingBlocked || v2Blocked) && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <ShieldAlert className="h-4 w-4 text-amber-600" /> Guardas activas (fallo cerrado)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-xs text-muted-foreground">
            {writesBlocked && (
              <p>
                Escrituras bloqueadas · <span className="font-mono">{data.writeGuard.code}</span> —{" "}
                {data.writeGuard.reason}
              </p>
            )}
            {v2Blocked && (
              <p>
                Escrituras REST v2 bloqueadas ·{" "}
                <span className="font-mono">{data.v2WriteGuard.code}</span> —{" "}
                {data.v2WriteGuard.reason}
              </p>
            )}
            {bookingBlocked && (
              <p>
                Reservas de prueba bloqueadas ·{" "}
                <span className="font-mono">{data.bookingGuard.code}</span> —{" "}
                {data.bookingGuard.reason}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Dry-run · Exportar tour propio a componentes v2
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Genera y valida el payload sin llamar a Bókun. La activación del producto queda siempre
            en borrador.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="h-10 min-w-[240px] rounded-md border border-input bg-background px-3 text-sm"
              value={tourId}
              onChange={(e) => setTourId(e.target.value)}
              aria-label="Tour a exportar"
            >
              <option value="">Selecciona un tour…</option>
              {(tours.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} {t.active ? "" : "(borrador)"}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={!tourId || dryMutation.isPending}
              onClick={() => dryMutation.mutate(tourId)}
            >
              <FlaskConical className="mr-1.5 h-3.5 w-3.5" />
              {dryMutation.isPending ? "Generando…" : "Ejecutar dry-run"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled
              title="Requiere BOKUN_TEST_WRITES_ENABLED=true"
            >
              Crear en Bókun test
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled
              title="Requiere BOKUN_TEST_WRITES_ENABLED=true"
            >
              Actualizar componentes
            </Button>
          </div>
          {dryMutation.isError && (
            <p className="text-sm text-destructive">{(dryMutation.error as Error).message}</p>
          )}
          {result && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant={result.valid ? "secondary" : "destructive"}>
                  {result.valid ? "Payload válido" : "Payload inválido"}
                </Badge>
                <Badge variant="secondary">{result.componentKeys.length} componentes</Badge>
                <Badge variant="secondary">Sin llamadas a Bókun</Badge>
              </div>
              {result.missingFields.length > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Campos faltantes: {result.missingFields.join(", ")}
                </p>
              )}
              {result.warnings.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-muted-foreground">
                  {result.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
              {result.errors.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-destructive">
                  {result.errors.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
              <pre className="max-h-96 overflow-auto rounded-md bg-muted/40 p-3 text-[11px] leading-relaxed">
                {result.payloadJson}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Cola de App Webhooks</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {data.queue.tableReady
              ? "Últimos eventos recibidos y su estado de procesamiento."
              : "La tabla aún no existe: aplica db/pending-migrations/20260918_bokun_webhook_events.sql cuando arranquen las pruebas."}
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            <Badge variant="secondary">Pendientes: {data.queue.pending}</Badge>
            <Badge variant="secondary">Procesados: {data.queue.processed}</Badge>
            <Badge variant="secondary">Dead-letter: {data.queue.deadLetter}</Badge>
          </div>
          {data.queue.recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin eventos registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="py-1.5 pr-3 font-medium">Recibido</th>
                    <th className="py-1.5 pr-3 font-medium">Topic</th>
                    <th className="py-1.5 pr-3 font-medium">Estado</th>
                    <th className="py-1.5 pr-3 font-medium">Intentos</th>
                    <th className="py-1.5 pr-3 font-medium">Vendor</th>
                    <th className="py-1.5 pr-3 font-medium">SellerExternalID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.queue.recent.map((e) => (
                    <tr key={e.id} className="border-t">
                      <td className="py-1.5 pr-3 font-mono">
                        {new Date(e.receivedAt).toLocaleString()}
                      </td>
                      <td className="py-1.5 pr-3">{e.topic}</td>
                      <td className="py-1.5 pr-3">{e.status}</td>
                      <td className="py-1.5 pr-3">{e.attempts}</td>
                      <td className="py-1.5 pr-3 font-mono">{e.vendorId ?? "—"}</td>
                      <td className="py-1.5 pr-3 font-mono">{e.sellerExternalId ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">SellerExternalID</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Row label="Configurado en la app">
            <span className="font-mono text-xs">
              {data.sellerExternalId.configuredInApp ?? "—"}
            </span>
          </Row>
          <Row label="Mapeos configurados">{data.sellerExternalId.mappedCount}</Row>
          <Row label="Recibido en eventos recientes">
            {data.sellerExternalId.receivedInRecentEvents}
          </Row>
          <Row label="Sin SellerExternalID">{data.sellerExternalId.missingInRecentEvents}</Row>
          <Row label="Vendedores sin mapear">
            {data.sellerExternalId.unmappedSellers.length === 0
              ? "—"
              : data.sellerExternalId.unmappedSellers.join(", ")}
          </Row>
          <p className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            Paso manual en Bókun test: Settings → Product extensions → Custom input fields →
            Contracts. Crear el Field Code exactamente{" "}
            <span className="font-mono">SellerExternalID</span> y asignar el ID a cada contrato del
            Marketplace. Si el contrato no tiene valor, Bókun no lo envía.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
